import { Inject, Injectable, NgZone, OnDestroy } from '@angular/core';
import { App } from '@capacitor/app';
import { COMMONS_ENV, NetworkService, StorageService } from '@damofire/data-angular-commons';
import { PartialDivision, Preference, PreferenceService } from '@damofire/data-stas';
import { setTag } from '@sentry/angular';
import { IndexedService, OFFLINE_WAREHOUSES_STORE, logger } from '@damofire/data-commons';
import { firstValueFrom, Observer, Subscription } from 'rxjs';
import { filter, takeWhile, tap } from 'rxjs/operators';
import { Warehouse } from '../warehouse/warehouse.dto';
import { WarehouseService } from '../warehouse/warehouse.service';
import { WAREHOUSE_SYNC_STEP_DOWNLOAD, WAREHOUSE_SYNC_STEP_PUSH, WAREHOUSE_SYNC_STEPS, WarehouseSyncContext, WarehouseSyncStep, WarehouseSyncStepContextEvent } from './executors';
import { WarehouseSyncStepExecutor } from './steps/warehouse-steps.executor';
import { WarehouseSyncStepFactory } from './steps/warehouse-steps.factory';
import { WarehouseSyncExecutor } from './steps/warehouse-sync.executor';
import { WarehouseSyncStepTaskExecutor } from './steps/warehouse-tasks.executor';
import { WarehouseSyncEventService } from './warehouse-sync-event.service';
import { AuthenticationService } from '@damofire/data-security';
import { PluginListenerHandle } from '@capacitor/core';

@Injectable()
export class WarehouseSyncService implements OnDestroy {
  private warehousesSync: WarehouseSyncContext;
  private appStateHandlePromise: Promise<PluginListenerHandle> | null = null;
  private networkSub: Subscription;
  private preferencesSub: Subscription;
  private syncInFlight = false;

  constructor(
    private readonly warehouseSyncStepFactory: WarehouseSyncStepFactory,
    private readonly preferenceService: PreferenceService,
    private readonly networkService: NetworkService,
    private readonly warehouseSyncEventService: WarehouseSyncEventService,
    private readonly idbService: IndexedService,
    private readonly warehouseService: WarehouseService,
    private readonly storageService: StorageService,
    private readonly ngZone: NgZone,
    @Inject(COMMONS_ENV) private readonly environment: any,
    private readonly authService: AuthenticationService,
  ) {
    this.init();
  }

  init(): void {
    if (this.environment.offline) {
      this.subscribeOnOfflineWarehouses();
    }

    this.subscribeOnConnection();
    this.subscribeOnAppState();
  }

  ngOnDestroy(): void {
    this.appStateHandlePromise?.then((h) => h.remove());
    this.networkSub?.unsubscribe();
    this.preferencesSub?.unsubscribe();
  }

  async currentWarehouseChanged(warehouse: Warehouse): Promise<void> {
    if (!this.warehousesSync || this.warehousesSync.warehouse !== warehouse._id) {
      clearTimeout(this.warehousesSync?.nextSyncInterval || 0);
      this.warehousesSync?.executor?.cancelSync();
      this.warehousesSync?.executorSubscription?.unsubscribe();
      this.syncInFlight = false;
      this.ngZone.runOutsideAngular(() => this.startSyncingWarehouse(warehouse._id)).catch(console.error);
    }
  }

  forceStartSync(warehouse: string, steps?: WarehouseSyncStep[]): void {
    this.ngZone.runOutsideAngular(() => this.startSyncingWarehouse(warehouse, true, steps)).catch(console.error);
  }

  forceStartTask(warehouse: string, step: WarehouseSyncStep, taskName: string): void {
    this.forceStartTasks(warehouse, step, [taskName]);
  }

  /**
   * Re-runs push/download tasks in isolation, sequentially (sync diagnostics
   * page) — e.g. picture listing followed by picture bytes.
   * Clean tasks are refused: they delete every row whose syncId differs from
   * the run's, so executing one outside a full sync would wipe its store.
   * Unlike launchSync this does not reschedule the periodic sync.
   */
  forceStartTasks(warehouse: string, step: WarehouseSyncStep, taskNames: string[]): void {
    if (step !== WAREHOUSE_SYNC_STEP_PUSH && step !== WAREHOUSE_SYNC_STEP_DOWNLOAD) {
      logger.error('Single-task re-run refused for clean step', { category: 'sync', warehouse, step, tasks: taskNames });
      return;
    }

    this.ngZone.runOutsideAngular(() => {
      firstValueFrom(this.preferenceService.listPreferences().pipe(filter((prefs: Preference<any>[]) => prefs.length > 0)))
        .then((userPreferences: Preference<unknown>[]) => {
        if (this.syncInFlight) {
          logger.info('Cancelling in-flight sync for single-task re-run', { category: 'sync', warehouse });
          this.warehousesSync?.executor?.cancelSync();
          this.warehousesSync?.executorSubscription?.unsubscribe();
        }
        this.syncInFlight = true;
        const syncStepTaskExecutor = new WarehouseSyncStepTaskExecutor();
        const syncStepExecutor = new WarehouseSyncStepExecutor(this.warehouseSyncStepFactory, syncStepTaskExecutor);
        const executor = new WarehouseSyncExecutor(syncStepExecutor);
        if (this.warehousesSync) {
          this.warehousesSync.executor = executor;
        }

        executor
          .executeSingleTasks({ steps: [step], division: warehouse, environment: this.environment, userPreferences }, step, taskNames)
          .pipe(
            takeWhile((e: WarehouseSyncStepContextEvent) => !!this.warehousesSync && e.ctx.division === warehouse),
            tap((e: WarehouseSyncStepContextEvent) => {
              if (e.event === 'SYNC_FINISHED' || e.event === 'SYNC_CANCELLED') {
                this.syncInFlight = false;
              }
              this.publishSyncStatus(e);
            }),
          )
          .subscribe({ error: console.error });
        })
        .catch(console.error);
    });
  }

  private subscribeOnConnection(): void {
    let previousState = this.networkService.connected;
    this.networkSub = this.networkService.status.pipe(filter((c) => c !== previousState)).subscribe(async (connected) => {
      previousState = connected;
      setTag('isOnline', connected);
      if (!connected) {
        logger.warn('Network lost, deactivating workers', { category: 'sync', warehouse: this.warehousesSync?.warehouse });
        this.unsubscribeFromAll();
      } else {
        await this.authService.authenticationLock();
        this.restoreSyncs();
      }
    });
  }

  private subscribeOnAppState(): void {
    if (this.environment.target === 'app') {
      this.appStateHandlePromise = App.addListener('appStateChange', async ({ isActive }) => {
        const connected = await this.networkService.isAppOnline();
        if (!isActive) {
          logger.info('App backgrounded, deactivating workers', { category: 'sync' });
          this.unsubscribeFromAll();
        } else if (connected) {
          this.restoreSyncs();
        }
      });
    } else {
      this.networkService.isAppOnline().then((connected) => {
        if (connected) {
          this.restoreSyncs();
        }
      });
    }
  }

  private unsubscribeFromAll(): void {
    logger.info('Unsubscribing from all syncs', { category: 'sync', warehouse: this.warehousesSync?.warehouse });

    if (this.warehousesSync) {
      this.storageService.setObject('sync-to-push', this.warehousesSync.warehouse);
      this.warehousesSync.executor?.cancelSync();
      this.warehousesSync.executorSubscription?.unsubscribe();
      clearTimeout(this.warehousesSync.nextSyncInterval);
      this.warehousesSync = null;
    }
    this.syncInFlight = false;
  }

  private restoreSyncs(): void {
    logger.debug('restoreSyncs called', { category: 'sync', hasExisting: !!this.warehousesSync });
    if (!this.warehousesSync && this.networkService.connected) {
      const warehouse = this.storageService.getObject('sync-to-push');
      this.warehousesSync = this.createNewWarehouseSync(warehouse, 0);

      const lastSync: WarehouseSyncStepContextEvent = this.storageService.getObject(`last-sync-${warehouse}`);
      this.launchSync(warehouse, lastSync ? [WAREHOUSE_SYNC_STEP_PUSH] : undefined);
      this.storageService.removeObject('sync-to-push');
    }
  }

  private attemptWarehouseSync(warehouse: string): void {
    if (this.networkService.connected) {
      this.startSyncingWarehouse(warehouse).catch(console.error);
    } else {
      logger.info('Sync skipped, device offline', { category: 'sync', warehouse });
    }
  }

  private async startSyncingWarehouse(warehouse: string, forceStart = false, steps?: WarehouseSyncStep[]): Promise<void> {
    const lastSync: WarehouseSyncStepContextEvent = this.storageService.getObject(`last-sync-${warehouse}`);
    const timeUntilNextSync = await this.timeUntilNextSync(lastSync);
    const nextSyncTime = forceStart ? 0 : timeUntilNextSync * 1000;

    if (forceStart) {
      // A forced sync preempts whatever is running; otherwise the guard in
      // launchSync would drop it and two executors would fight over IDB.
      this.warehousesSync?.executor?.cancelSync();
      this.warehousesSync?.executorSubscription?.unsubscribe();
      this.syncInFlight = false;
    }

    logger.info(`Sync planned in ${nextSyncTime}ms`, { category: 'sync', warehouse, nextSyncTime });
    const nextSyncInterval = window.setTimeout(() => this.launchSync(warehouse, steps), nextSyncTime);

    if (nextSyncTime > 0) {
      this.publishSyncStatus(lastSync);
    }

    this.warehousesSync = this.createNewWarehouseSync(warehouse, nextSyncInterval);
  }

  private launchSync(warehouse: string, steps: WarehouseSyncStep[] = WAREHOUSE_SYNC_STEPS): void {
    if (this.syncInFlight) {
      logger.info('Sync already in flight, skipping duplicate launch', { category: 'sync', warehouse });
      return;
    }
    this.syncInFlight = true;
    const syncStepTaskExecutor = new WarehouseSyncStepTaskExecutor();
    const syncStepExecutor = new WarehouseSyncStepExecutor(this.warehouseSyncStepFactory, syncStepTaskExecutor);
    this.warehousesSync.executor = new WarehouseSyncExecutor(syncStepExecutor);
    this.storageService.removeObject(`last-sync-${warehouse}`);

    const obs: Observer<WarehouseSyncStepContextEvent> = {
      next: (ctx: WarehouseSyncStepContextEvent) => this.rescheduleSyncingWarehouse(warehouse, ctx),
      error: console.error,
      complete: () => {
        logger.info('Sync completed', { category: 'sync' });
      },
    };

    firstValueFrom(this.preferenceService.listPreferences().pipe(filter((prefs: Preference<any>[]) => prefs.length > 0))).then((userPreferences: Preference<unknown>[]) => {
      const envClone = structuredClone(this.environment);
      envClone['custom-token'] = sessionStorage.getItem('custom-token');

      const sub = this.warehousesSync.executor
        .execute({ steps, division: warehouse, environment: this.environment, userPreferences })
        .pipe(
          takeWhile((e: WarehouseSyncStepContextEvent) => !!this.warehousesSync && e.ctx.division === this.warehousesSync.warehouse),
          tap((e: WarehouseSyncStepContextEvent) => {
            if (e.event === 'SYNC_FINISHED' || e.event === 'SYNC_CANCELLED') {
              this.syncInFlight = false;
            }
            this.publishSyncStatus(e);
          }),
          filter((e: WarehouseSyncStepContextEvent) => e.event === 'SYNC_FINISHED' || e.event === 'SYNC_CANCELLED'),
        )
        .subscribe(obs);
      this.warehousesSync.executorSubscription = sub;
    });
  }

  private publishSyncStatus(e: WarehouseSyncStepContextEvent): void {
    this.ngZone.run(() => this.warehouseSyncEventService.publishSyncStatus(e));
  }

  private rescheduleSyncingWarehouse(warehouse: string, ctx: WarehouseSyncStepContextEvent): void {
    // Only terminal events reschedule. This used to run on every event, which
    // unsubscribed the live sync subscription on its first progress event and
    // orphaned the running executor (the exact double-sync mess in the logs).
    if (ctx.event !== 'SYNC_FINISHED' && ctx.event !== 'SYNC_CANCELLED') {
      return;
    }
    if (ctx.event !== 'SYNC_CANCELLED') {
      this.storageService.setObject(`last-sync-${warehouse}`, ctx);
    }

    const nextSyncTime = this.getMinSyncDelay() * 1000;
    const nextSyncInterval = window.setTimeout(() => {
      logger.info('Sync timeout reached, launching sync', { category: 'sync', warehouse });
      this.attemptWarehouseSync(warehouse);
    }, nextSyncTime);
    logger.info('Next sync scheduled', { category: 'sync', warehouse, nextSyncTime });
    this.warehousesSync = this.createNewWarehouseSync(warehouse, nextSyncInterval);
  }

  private createNewWarehouseSync(warehouse: string, nextSyncInterval: number): WarehouseSyncContext {
    this.warehousesSync?.executorSubscription?.unsubscribe();
    return { warehouse, nextSyncInterval };
  }

  private getMinSyncDelay(): number {
    return this.environment.appConfig.watson.minSyncingDelay || 3600;
  }

  private async timeUntilNextSync(lastSync: WarehouseSyncStepContextEvent): Promise<number> {
    let timeUntil = 0;

    if (lastSync) {
      logger.debug('Evaluating time since last sync', { category: 'sync', warehouse: lastSync.ctx.division });
      const timeSinceLastSync = (new Date().getTime() - lastSync.ctx.when) / 1000;
      const minSyncingDelay = this.getMinSyncDelay();
      timeUntil = minSyncingDelay - timeSinceLastSync;
      logger.info('Time until next sync evaluated', { category: 'sync', warehouse: lastSync.ctx.division, timeUntil, timeSinceLastSync, minSyncingDelay });
    }

    return timeUntil;
  }

  private subscribeOnOfflineWarehouses(): void {
    this.preferencesSub?.unsubscribe();
    this.preferencesSub = this.preferenceService.offlineWarehouses().subscribe(async (preferences) => {
      const ids: string[] = [];
      for (const p of preferences) {
        if (p.changeType === 'removed') {
          await this.idbService.delete(OFFLINE_WAREHOUSES_STORE, p._id);
        } else if (p.changeType === 'added') {
          const division: PartialDivision = p.value;
          ids.push(division._id);

          if (!(await this.idbService.exists(OFFLINE_WAREHOUSES_STORE, division._id))) {
            await this.idbService.add(OFFLINE_WAREHOUSES_STORE, { _id: division._id });
          }
        }
      }

      const allFromStore = await this.warehouseService.getOfflineWarehouses();
      const missingKeys = allFromStore.reduce((p, c) => {
        if (!ids.includes(c._id)) {
          p.push(c._id);
        }

        return p;
      }, []);

      if (missingKeys.length) {
        await this.idbService.deleteAll(OFFLINE_WAREHOUSES_STORE, missingKeys);
      }
    });
  }
}
